// Date-range presets for the invoices filter bar, modelled on QuickBooks'.
//
// Deliberately pure and free of `new Date()` with no argument: `today` is
// always a parameter, so every boundary -- month ends, quarter edges, the
// fiscal year rollover -- is a unit test rather than something you can only
// observe by changing the system clock.
//
// Everything in and out is a "YYYY-MM-DD" string. All arithmetic goes through
// UTC to avoid a local timezone shifting a date across midnight.

export const DATE_PRESETS = [
  { key: "all",              label: "All Time" },
  { key: "this_month",       label: "This Month" },
  { key: "last_month",       label: "Last Month" },
  { key: "this_quarter",     label: "This Quarter" },
  { key: "last_quarter",     label: "Last Quarter" },
  { key: "this_year",        label: "This Year" },
  { key: "last_year",        label: "Last Year" },
  { key: "this_fiscal_year", label: "This Fiscal Year (Firm)" },
  { key: "last_fiscal_year", label: "Last Fiscal Year (Firm)" },
  { key: "last_30",          label: "Last 30 Days" },
  { key: "last_90",          label: "Last 90 Days" },
  { key: "custom",           label: "Custom" },
];

// The presets that mean nothing without a firm fiscal year. InvoicesPage hides
// these when the settings fetch fails.
export const FISCAL_PRESET_KEYS = ["this_fiscal_year", "last_fiscal_year"];

const iso = (d) => d.toISOString().slice(0, 10);
const utc = (y, m, day) => new Date(Date.UTC(y, m - 1, day));
const parse = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return utc(y, m, d);
};
// Day 0 of the NEXT month is the last day of this one, which is how February
// and the 30/31 split are handled without a lookup table.
const lastDayOf = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const addDays = (d, n) => new Date(d.getTime() + n * 86400000);

const monthOf = (d) => d.getUTCMonth() + 1;
const yearOf = (d) => d.getUTCFullYear();

// The fiscal year CONTAINING `d`, given the month it ends in. Returns the
// calendar year the FY ends in.
//
// With a June end and d in August 2026, the current FY ends June 2027; with d
// in March 2026 it ends June 2026. A December end makes this the calendar year.
const fiscalYearEnding = (d, endMonth) =>
  monthOf(d) <= endMonth ? yearOf(d) : yearOf(d) + 1;

const fiscalYearRange = (endingYear, endMonth) => {
  const end = utc(endingYear, endMonth, lastDayOf(endingYear, endMonth));
  // The day after the previous year end.
  const start = addDays(utc(endingYear - 1, endMonth, lastDayOf(endingYear - 1, endMonth)), 1);
  return { start: iso(start), end: iso(end) };
};

// The caller's LOCAL calendar date as "YYYY-MM-DD". `rangeFor` works in UTC
// internally, but "today" must mean the user's today: toISOString() would give
// the UTC date, which for a user west of UTC is already tomorrow for the last
// hours of every local day -- so at 18:00 on 30 June, "Last Fiscal Year" would
// return the year that is still open.
export function todayIsoLocal(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2024-07-01" -> "Jul 1, 2024", read straight off the string so no timezone
// can move it a day.
const fmtDay = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};

// The dates a filter actually covers, spelled out -- "Last Fiscal Year" alone
// left the owner guessing which twelve months it meant. Either end may be
// blank (Custom with one box filled); both blank is no range at all.
export function formatRange(start, end) {
  if (start && end) return `${fmtDay(start)} – ${fmtDay(end)}`;
  if (start) return `from ${fmtDay(start)}`;
  if (end) return `through ${fmtDay(end)}`;
  return "";
}

export function rangeFor(key, today, fiscalEndMonth = 12) {
  const d = parse(today);
  const y = yearOf(d);
  const m = monthOf(d);

  switch (key) {
    case "this_month":
      return { start: iso(utc(y, m, 1)), end: iso(d) };

    case "last_month": {
      const pm = m === 1 ? 12 : m - 1;
      const py = m === 1 ? y - 1 : y;
      return { start: iso(utc(py, pm, 1)), end: iso(utc(py, pm, lastDayOf(py, pm))) };
    }

    case "this_quarter": {
      const qStart = Math.floor((m - 1) / 3) * 3 + 1;
      return { start: iso(utc(y, qStart, 1)), end: iso(d) };
    }

    case "last_quarter": {
      const qStart = Math.floor((m - 1) / 3) * 3 + 1;
      const pqStart = qStart === 1 ? 10 : qStart - 3;
      const pqYear = qStart === 1 ? y - 1 : y;
      const pqEnd = pqStart + 2;
      return {
        start: iso(utc(pqYear, pqStart, 1)),
        end: iso(utc(pqYear, pqEnd, lastDayOf(pqYear, pqEnd))),
      };
    }

    case "this_year":
      return { start: iso(utc(y, 1, 1)), end: iso(d) };

    case "last_year":
      return { start: iso(utc(y - 1, 1, 1)), end: iso(utc(y - 1, 12, 31)) };

    case "this_fiscal_year": {
      const { start } = fiscalYearRange(fiscalYearEnding(d, fiscalEndMonth), fiscalEndMonth);
      return { start, end: iso(d) };   // to date, not to the future year end
    }

    case "last_fiscal_year":
      return fiscalYearRange(fiscalYearEnding(d, fiscalEndMonth) - 1, fiscalEndMonth);

    case "last_30":
      return { start: iso(addDays(d, -29)), end: iso(d) };

    case "last_90":
      return { start: iso(addDays(d, -89)), end: iso(d) };

    case "custom":
      // The caller keeps whatever the two date inputs already hold.
      return { start: null, end: null };

    case "all":
    default:
      return { start: "", end: "" };
  }
}
